import { useCallback, useEffect, useState } from "react";
import { Truck, Bike, ListChecks, IndianRupee, Wallet, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const inr = (v) => `\u20b9${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v || 0).toLocaleString("en-IN");
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };

const Card = ({ icon: Icon, label, value, sub, tone = "teal" }) => {
  const tones = { teal: "text-teal-700", slate: "text-slate-700", indigo: "text-indigo-600", emerald: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" };
  return (
    <div className="rounded-xl border bg-white p-3" data-testid={`dpr-sum-card-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase text-slate-400"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className={`mt-1 text-xl font-bold break-all ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
};

export default function DprDashboardSummary() {
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [pickMode, setPickMode] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!pid || !from || !to) return;
    setLoading(true);
    api.get("/dpr/dashboard-summary", { params: { project_id: pid, date_from: from, date_to: to } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [pid, from, to]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-8" data-testid="dashboard-section-dpr">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-slate-900">DPR Work Summary</h2>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1 pb-0.5">
            <Button data-testid="dpr-sum-today" size="sm" variant="outline" className="h-8" disabled={!pid} onClick={() => { const t = today(); setFrom(t); setTo(t); }}>Today</Button>
            <Button data-testid="dpr-sum-yesterday" size="sm" variant="outline" className="h-8" disabled={!pid} onClick={() => { const y = yesterday(); setFrom(y); setTo(y); }}>Yesterday</Button>
            <Button data-testid="dpr-sum-pick-toggle" size="sm" variant={pickMode ? "default" : "outline"} className={`h-8 ${pickMode ? "bg-teal-700 hover:bg-teal-800" : ""}`} disabled={!pid} onClick={() => setPickMode((s) => !s)}>Pick Date</Button>
          </div>
          {pickMode && (
            <div><Label className="text-xs text-slate-500">Date</Label><Input data-testid="dpr-sum-day" type="date" value={from === to ? from : today()} onChange={(e) => { setFrom(e.target.value); setTo(e.target.value); }} className="mt-1 h-8 w-40" /></div>
          )}
          <div><Label className="text-xs text-slate-500">From</Label><Input data-testid="dpr-sum-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-8 w-40" /></div>
          <div><Label className="text-xs text-slate-500">To</Label><Input data-testid="dpr-sum-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-8 w-40" /></div>
          <Button data-testid="dpr-sum-apply" size="sm" className="h-8 bg-teal-700 hover:bg-teal-800" onClick={load} disabled={loading || !pid}>{loading ? "Loading…" : "Apply"}</Button>
          <Button data-testid="dpr-sum-clear" size="sm" variant="ghost" className="h-8 text-slate-500 hover:text-slate-800" title="Reset to this month" disabled={!pid} onClick={() => { setFrom(monthStart()); setTo(today()); setPickMode(false); }}>Clear</Button>
        </div>
      </div>
      {!pid ? (
        <p className="rounded-xl border bg-white px-4 py-6 text-sm text-slate-400" data-testid="dpr-sum-need-project">Select a Project (top bar) to view the DPR work summary.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
          <Card icon={Truck} label="Vehicle Team Progress" value={num(data?.vehicle_work)} sub={`${num(data?.vehicle_team_days)} team-days`} tone="teal" />
          <Card icon={Bike} label="Bike Team Progress" value={num(data?.bike_work)} sub={`${num(data?.bike_team_days)} team-days`} tone="indigo" />
          <Card icon={ListChecks} label="Total Work Count" value={num(data?.work_count)} sub={`${num(data?.active_days)} active days`} tone="slate" />
          <Card icon={IndianRupee} label="Amount of Work (RC)" value={inr(data?.revenue)} sub="Revenue as per RC" tone="teal" />
          <Card icon={Wallet} label="Total Expenditure" value={inr(data?.cost)} sub="Team + vehicle cost" tone="amber" />
          <Card icon={IndianRupee} label="Revenue" value={inr(data?.revenue)} tone="emerald" />
          <Card icon={TrendingUp} label={Number(data?.margin) >= 0 ? "Profit" : "Loss"} value={inr(data?.margin)} tone={Number(data?.margin) >= 0 ? "emerald" : "red"} />
        </div>
      )}
    </div>
  );
}
